import { ArrowLeft, ExternalLink, Link2, Paperclip, Send, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { RefreshCw } from "lucide-react";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type Message = {
  id: string;
  direction: "inbound" | "outbound";
  content: string;
  contentHtml: string | null;
  senderEmail: string;
  senderName: string | null;
  senderUserId: string | null;
  senderUserName: string | null;
  externalMessageId: string | null;
  createdAt: string;
  attachments: {
    id: string | null;
    filename: string;
    contentType: string | null;
    downloadUrl: string;
  }[];
};

type TicketDetail = {
  id: string;
  fromEmail: string;
  fromName: string | null;
  subject: string;
  status: string;
  priority: string;
  assignedTo: string | null;
  linkedAuctionId: string | null;
  linkedOrganizationId: string | null;
  linkedUserId: string | null;
  lastMessageAt: string;
  createdAt: string;
  messages: Message[];
};

type SearchResult = {
  id: string;
  label: string;
  sublabel?: string;
};

export function SupportTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [acting, setActing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!id) return;
    fetch(`${API}/api/admin/support-tickets/${id}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .finally(() => setLoading(false));
  }, [id]);

  async function sendReply() {
    if (!id || !reply.trim()) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("content", reply.trim());
      for (const file of replyFiles) {
        formData.append("files", file);
      }

      const r = await fetch(`${API}/api/admin/support-tickets/${id}/reply`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (r.ok) {
        const msg = await r.json();
        setData((d) => d ? {
          ...d,
          messages: [
            ...d.messages,
            {
              contentHtml: null,
              senderName: null,
              senderUserName: null,
              externalMessageId: null,
              attachments: [],
              ...msg,
            },
          ],
        } : d);
        setReply("");
        setReplyFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      }
    } finally {
      setSending(false);
    }
  }

  async function updateTicket(patch: Record<string, unknown>) {
    if (!id) return;
    setActing(true);
    try {
      const r = await fetch(`${API}/api/admin/support-tickets/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (r.ok) {
        const updated = await r.json();
        setData((d) => d ? { ...d, ...updated } : d);
      }
    } finally {
      setActing(false);
    }
  }

  async function refreshThread() {
    if (!id) return;
    setRefreshing(true);
    try {
      const r = await fetch(`${API}/api/admin/support-tickets/${id}/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (r.ok) {
        const refreshed = await r.json();
        setData(refreshed);
      }
    } finally {
      setRefreshing(false);
    }
  }

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto">
        <p className="text-sm text-muted-foreground">Laster...</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto">
        <p className="text-sm text-destructive">Henvendelsen ble ikke funnet.</p>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/support")} className="mt-0.5 flex-shrink-0">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-black tracking-tight truncate" style={{ fontFamily: "var(--font-display)" }}>
              {data.subject}
            </h1>
            <StatusBadge status={data.status} />
            <PriorityBadge priority={data.priority} />
            <Button variant="outline" onClick={refreshThread} disabled={refreshing} className="ml-auto">
              <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Oppdater
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {data.fromName ? `${data.fromName} <${data.fromEmail}>` : data.fromEmail}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — message thread + reply */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardContent className="p-4 space-y-4">
              {data.messages.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen meldinger ennå.</p>
              ) : (
                data.messages.map((msg) => (
                  <MessageBubble key={msg.id} message={msg} />
                ))
              )}
              <div ref={bottomRef} />
            </CardContent>
          </Card>

          {/* Reply box */}
          {data.status !== "closed" && (
            <Card>
              <CardContent className="p-4 space-y-3">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Svar til {data.fromEmail}</p>
                <textarea
                  className="w-full min-h-[120px] rounded-lg border border-border bg-background px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Skriv et svar..."
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendReply();
                  }}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const nextFiles = Array.from(e.target.files || []);
                    if (nextFiles.length === 0) return;
                    setReplyFiles((current) => [...current, ...nextFiles]);
                    e.currentTarget.value = "";
                  }}
                />
                {replyFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {replyFiles.map((file, index) => (
                      <div key={`${file.name}-${index}`} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs">
                        <Paperclip className="h-3 w-3" />
                        <span className="max-w-[220px] truncate">{file.name}</span>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={() => setReplyFiles((current) => current.filter((_, currentIndex) => currentIndex !== index))}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Cmd+Enter for å sende</span>
                    <Button type="button" size="sm" variant="outline" className="gap-2" onClick={() => fileInputRef.current?.click()}>
                      <Paperclip className="h-3.5 w-3.5" />
                      Legg ved
                    </Button>
                  </div>
                  <Button size="sm" className="gap-2" disabled={sending || (!reply.trim() && replyFiles.length === 0)} onClick={sendReply}>
                    <Send className="h-3.5 w-3.5" />
                    {sending ? "Sender..." : "Send svar"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right — controls */}
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold">Status</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {["open", "in_progress", "resolved", "closed"].map((s) => (
                <button
                  key={s}
                  disabled={acting || data.status === s}
                  onClick={() => updateTicket({ status: s })}
                  className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors border ${data.status === s
                    ? "border-primary bg-primary/5 font-medium"
                    : "border-border hover:bg-muted/40"
                    }`}
                >
                  <StatusLabel status={s} />
                  {data.status === s && <span className="text-xs text-primary">Nåværende</span>}
                </button>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold">Prioritet</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {["low", "normal", "high", "urgent"].map((p) => (
                <button
                  key={p}
                  disabled={acting || data.priority === p}
                  onClick={() => updateTicket({ priority: p })}
                  className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors border ${data.priority === p
                    ? "border-primary bg-primary/5 font-medium"
                    : "border-border hover:bg-muted/40"
                    }`}
                >
                  <PriorityLabel priority={p} />
                  {data.priority === p && <span className="text-xs text-primary">Nåværende</span>}
                </button>
              ))}
            </CardContent>
          </Card>

          <EntityLinksCard
            ticketId={data.id}
            linkedAuctionId={data.linkedAuctionId}
            linkedOrganizationId={data.linkedOrganizationId}
            linkedUserId={data.linkedUserId}
            onUpdate={(patch) => setData((d) => d ? { ...d, ...patch } : d)}
          />

          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold">Detaljer</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <MetaRow label="Opprettet">{formatDateTime(data.createdAt)}</MetaRow>
              <MetaRow label="Siste melding">{formatDateTime(data.lastMessageAt)}</MetaRow>
              <MetaRow label="Meldinger">{data.messages.length}</MetaRow>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ─── Entity Links Card ─── */

const ENTITY_CONFIGS = {
  auction: { label: "Auksjon", listPath: "/api/admin/auctions", navPath: "/auksjoner", patchKey: "linkedAuctionId" },
  organization: { label: "Organisasjon", listPath: "/api/admin/organizations", navPath: "/organisasjoner", patchKey: "linkedOrganizationId" },
  user: { label: "Bruker", listPath: "/api/admin/users", navPath: "/brukere", patchKey: "linkedUserId" },
} as const;

type EntityKind = keyof typeof ENTITY_CONFIGS;

function EntityLinksCard({
  ticketId,
  linkedAuctionId,
  linkedOrganizationId,
  linkedUserId,
  onUpdate,
}: {
  ticketId: string;
  linkedAuctionId: string | null;
  linkedOrganizationId: string | null;
  linkedUserId: string | null;
  onUpdate: (patch: Partial<Record<"linkedAuctionId" | "linkedOrganizationId" | "linkedUserId", string | null>>) => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Link2 className="h-3.5 w-3.5" />
          Koblinger
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {(["auction", "organization", "user"] as EntityKind[]).map((kind) => {
          const cfg = ENTITY_CONFIGS[kind];
          const currentId = kind === "auction" ? linkedAuctionId : kind === "organization" ? linkedOrganizationId : linkedUserId;
          return (
            <EntityRow
              key={kind}
              kind={kind}
              ticketId={ticketId}
              currentId={currentId}
              cfg={cfg}
              onUpdate={(id) => onUpdate({ [cfg.patchKey]: id })}
            />
          );
        })}
      </CardContent>
    </Card>
  );
}

function EntityRow({
  kind,
  ticketId,
  currentId,
  cfg,
  onUpdate,
}: {
  kind: EntityKind;
  ticketId: string;
  currentId: string | null;
  cfg: (typeof ENTITY_CONFIGS)[EntityKind];
  onUpdate: (id: string | null) => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [saving, setSaving] = useState(false);

  async function fetchResults(q: string) {
    setLoadingResults(true);
    try {
      const res = await fetch(`${API}${cfg.listPath}`, { credentials: "include" });
      if (!res.ok) return;
      const list = await res.json() as any[];
      const lq = q.toLowerCase();
      setResults(
        list
          .filter((item) => {
            if (kind === "auction") return !lq || item.title?.toLowerCase().includes(lq) || item.make?.toLowerCase().includes(lq);
            if (kind === "organization") return !lq || item.name?.toLowerCase().includes(lq) || item.orgNumber?.includes(lq);
            return !lq || item.name?.toLowerCase().includes(lq) || item.email?.toLowerCase().includes(lq);
          })
          .slice(0, 8)
          .map((item): SearchResult => ({
            id: item.id,
            label: kind === "auction" ? (item.title || `${item.make} ${item.model}`) : (item.name || item.email),
            sublabel: kind === "auction" ? item.orgName : kind === "organization" ? item.orgNumber : item.email,
          })),
      );
    } finally {
      setLoadingResults(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchResults(query), query ? 300 : 0);
    return () => clearTimeout(t);
  }, [query, open]);

  async function save(id: string | null) {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/support-tickets/${ticketId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [cfg.patchKey]: id }),
      });
      if (r.ok) {
        onUpdate(id);
        setOpen(false);
        setQuery("");
        setResults([]);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{cfg.label}</span>
        <div className="flex items-center gap-1">
          {currentId && (
            <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => navigate(`${cfg.navPath}/${currentId}`)}>
              <ExternalLink className="h-3 w-3" />
            </Button>
          )}
          {currentId && (
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 text-muted-foreground hover:text-destructive"
              disabled={saving}
              onClick={() => save(null)}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          {!open && (
            <Button variant="ghost" size="sm" className="h-5 text-xs px-1.5" onClick={() => setOpen(true)}>
              {currentId ? "Endre" : "Legg til"}
            </Button>
          )}
        </div>
      </div>

      {currentId && !open && (
        <p className="text-xs font-mono text-muted-foreground truncate px-0.5">{currentId}</p>
      )}

      {open && (
        <div className="space-y-1.5 pt-0.5">
          <input
            type="text"
            className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="Søk..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="space-y-0.5 max-h-40 overflow-y-auto">
            {loadingResults ? (
              <p className="text-xs text-muted-foreground px-1 py-1">Laster...</p>
            ) : results.length === 0 ? (
              <p className="text-xs text-muted-foreground px-1 py-1">Ingen treff</p>
            ) : (
              results.map((r) => (
                <button
                  key={r.id}
                  disabled={saving}
                  onClick={() => save(r.id)}
                  className="w-full text-left rounded-lg px-2.5 py-1.5 text-sm hover:bg-muted/40 transition-colors"
                >
                  <p className="font-medium truncate text-xs">{r.label}</p>
                  {r.sublabel && <p className="text-[11px] text-muted-foreground truncate">{r.sublabel}</p>}
                </button>
              ))
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-6 text-xs text-muted-foreground"
            onClick={() => { setOpen(false); setQuery(""); setResults([]); }}
          >
            Avbryt
          </Button>
        </div>
      )}
    </div>
  );
}

/* ─── Shared components ─── */

function MessageBubble({ message }: { message: Message }) {
  const isOutbound = message.direction === "outbound";
  const displayContent = getMessageDisplayContent(message);

  return (
    <div className={`flex ${isOutbound ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 space-y-1 ${isOutbound
          ? "bg-primary text-white rounded-br-sm"
          : "bg-muted text-foreground rounded-bl-sm"
          }`}
      >
        <p className="text-xs font-medium opacity-70">
          {isOutbound
            ? (message.senderUserName || "Admin")
            : (message.senderName || message.senderEmail)}
        </p>
        <p className="text-sm whitespace-pre-wrap break-words">{displayContent}</p>
        {message.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {message.attachments.map((attachment) => (
              <a
                key={`${message.id}-${attachment.id || attachment.filename}`}
                href={attachment.downloadUrl}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${isOutbound
                  ? "border-white/25 bg-white/10 text-white hover:bg-white/15"
                  : "border-border bg-background text-foreground hover:bg-background/80"
                  }`}
              >
                <Paperclip className="h-3 w-3" />
                <span className="max-w-[220px] truncate">{attachment.filename}</span>
              </a>
            ))}
          </div>
        )}
        <p className="text-[11px] opacity-50 text-right">{formatDateTime(message.createdAt)}</p>
      </div>
    </div>
  );
}

function getMessageDisplayContent(message: Message): string {
  const plainContent = message.content.trim();
  if (plainContent && plainContent !== "[Ingen innhold]") {
    return plainContent;
  }

  const htmlFallback = htmlToDisplayText(message.contentHtml);
  if (htmlFallback) {
    return htmlFallback;
  }

  return plainContent || "[Ingen innhold]";
}

function htmlToDisplayText(html: string | null): string {
  if (!html || typeof window === "undefined") {
    return "";
  }

  const normalizedHtml = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|section|article|h[1-6])>/gi, "\n");

  const parser = new DOMParser();
  const doc = parser.parseFromString(normalizedHtml, "text/html");
  const text = doc.body.textContent ?? "";

  return text
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]+\n/g, "\n")
    .trim();
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-muted-foreground flex-shrink-0">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; variant: any }> = {
    open: { label: "Åpen", variant: "warning" },
    in_progress: { label: "Under behandling", variant: "secondary" },
    resolved: { label: "Løst", variant: "success" },
    closed: { label: "Lukket", variant: "muted" },
  };
  const { label, variant } = map[status] ?? { label: status, variant: "muted" };
  return <Badge variant={variant}>{label}</Badge>;
}

function PriorityBadge({ priority }: { priority: string }) {
  const map: Record<string, { label: string; variant: any }> = {
    urgent: { label: "Urgent", variant: "destructive" },
    high: { label: "Høy", variant: "destructive" },
    normal: { label: "Normal", variant: "muted" },
    low: { label: "Lav", variant: "muted" },
  };
  const { label, variant } = map[priority] ?? { label: priority, variant: "muted" };
  return <Badge variant={variant}>{label}</Badge>;
}

function StatusLabel({ status }: { status: string }) {
  const map: Record<string, string> = {
    open: "Åpen",
    in_progress: "Under behandling",
    resolved: "Løst",
    closed: "Lukket",
  };
  return <span>{map[status] ?? status}</span>;
}

function PriorityLabel({ priority }: { priority: string }) {
  const map: Record<string, string> = {
    low: "Lav",
    normal: "Normal",
    high: "Høy",
    urgent: "Urgent",
  };
  return <span>{map[priority] ?? priority}</span>;
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("nb-NO", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}
